import { useMemo } from 'react'
import { omit, pickBy, values } from 'lodash'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { FileValidationStatus } from '@/schema'
import { chunkArray, timeout } from '@/utils/common'
import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  UPLOAD_DELAY_MS,
  UPLOAD_FILE_BATCH_SIZE,
} from '@/utils/constants'

import { fileUploadApi } from './services'
import {
  DeliverableSetOption,
  FileUploadFilters,
  UploadFile,
  UploadPhase,
  VersionStatus,
} from './types'
import {
  getDocumentTypeFromFilename,
  getNewFileVersionStatus,
  getNewVersionStatus,
  validateFileExtension,
} from './utils'
import { ExtractionError } from '.'

interface FileUploadStore {
  files: Record<string, UploadFile>
  selectedDeliverableSet: DeliverableSetOption | null
  isUploading: boolean
  uploadProgress: number
  error: string | null
  showFloatingIndicator: boolean
  filters: FileUploadFilters
  contextDeliverableSetId: string | null
  uploadPhase: UploadPhase
  uploadRoutePath: string | null
  referrerRoute: string | null

  addFiles: (newFiles: File[], deliverableSetId?: string) => void
  addErrorFiles: (errorFiles: ExtractionError[]) => void
  removeFile: (fileId: string) => void
  cancelFileValidation: (fileId: string) => void
  removeFailedAndCancelledFiles: () => void
  retryFailedFiles: () => void
  clearFiles: () => void
  updateFile: (fileId: string, updates: Partial<UploadFile>) => void
  updateFileType: (fileId: string, documentType: string) => Promise<void>
  setSelectedDeliverableSet: (
    deliverableSet: DeliverableSetOption | null
  ) => void
  startUpload: () => void
  updateUploadProgress: (progress: number) => void
  completeUpload: () => void
  cancelUpload: () => void
  setError: (error: string | null) => void
  clearError: () => void
  setFloatingIndicator: (show: boolean) => void
  setFilter: (filter: Partial<FileUploadFilters>) => void
  resetFilters: () => void
  setContextDeliverableSetId: (id: string | null) => void
  setUploadPhase: (phase: UploadPhase) => void
  setUploadRoutePath: (path: string | null) => void
  setReferrerRoute: (route: string | null) => void
  reset: () => void
}

const initialState = {
  files: {} as Record<string, UploadFile>,
  selectedDeliverableSet: null as DeliverableSetOption | null,
  isUploading: false,
  uploadProgress: 0,
  error: null as string | null,
  showFloatingIndicator: false,
  filters: {
    searchText: '',
    status: 'all' as FileUploadFilters['status'],
  },
  contextDeliverableSetId: null as string | null,
  uploadPhase: UploadPhase.INITIAL,
  uploadRoutePath: null as string | null,
  referrerRoute: null as string | null,
}

const validateFile = (
  file: File
): {
  validationStatus: FileValidationStatus
  versionStatus?: VersionStatus
  errorMessage?: string
} => {
  const allowedTypes = ALLOWED_DOCUMENT_EXTENSIONS

  if (!validateFileExtension(file, allowedTypes)) {
    return {
      validationStatus: FileValidationStatus.FAILED,
      errorMessage: 'Unsupported File Format',
    }
  }

  return {
    validationStatus: FileValidationStatus.READY_TO_UPLOAD,
  }
}

export const useFileUploadStore = create<FileUploadStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        addFiles: (newFiles: File[], deliverableSetId?: string) => {
          const currentFiles = get().files

          if (newFiles.length === 0) {
            return
          }

          const pendingFilesRecord: Record<string, UploadFile> = {}
          const pendingFilesList: UploadFile[] = []

          const newFileNameCounts = new Map<string, number>()
          newFiles.forEach((file) => {
            const count = newFileNameCounts.get(file.name) || 0
            newFileNameCounts.set(file.name, count + 1)
          })

          const existingFilesByName = new Map<string, UploadFile[]>()
          values(currentFiles).forEach((existingFile) => {
            const files = existingFilesByName.get(existingFile.fileName) || []
            files.push(existingFile)
            existingFilesByName.set(existingFile.fileName, files)
          })

          const fileNameIndices = new Map<string, number>()
          const baseTimestamp = Date.now()

          const filesToUpdate: Record<string, Partial<UploadFile>> = {}

          newFiles.forEach((file, globalIndex) => {
            const isDuplicateInBatch =
              (newFileNameCounts.get(file.name) || 0) > 1

            // Only check duplicates against ACTIVE files (exclude FAILED/CANCELLED)
            const activeExistingFiles = (
              existingFilesByName.get(file.name) || []
            ).filter(
              (f) =>
                f.validationStatus !== FileValidationStatus.FAILED &&
                f.validationStatus !== FileValidationStatus.CANCELLED
            )
            const isDuplicateWithExisting = activeExistingFiles.length > 0
            const isDuplicate = isDuplicateInBatch || isDuplicateWithExisting

            if (isDuplicateWithExisting) {
              activeExistingFiles.forEach((existingFile) => {
                filesToUpdate[existingFile.id] = {
                  validationStatus: FileValidationStatus.FAILED,
                  errorMessage: 'Duplicate File Name.',
                  documentType: '',
                  versionStatus: undefined,
                  isActionRequired: false,
                }
              })
            }

            const index = fileNameIndices.get(file.name) || 0
            fileNameIndices.set(file.name, index + 1)

            const randomValue = crypto
              .getRandomValues(new Uint32Array(1))[0]
              .toString(36)
            const uniqueId = isDuplicate
              ? `${file.name}_dup_${index}_${globalIndex}_${baseTimestamp}_${randomValue}`
              : file.name

            const uploadFile: UploadFile = {
              id: uniqueId,
              file,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              validationStatus: isDuplicate
                ? FileValidationStatus.FAILED
                : FileValidationStatus.VALIDATING,
              uploadProgress: 0,
              documentType: '',
              requiresManualType: false,
              errorMessage: isDuplicate ? 'Duplicate File Name.' : undefined,
              versionStatus: undefined,
              isActionRequired: isDuplicate ? false : undefined,
            }
            pendingFilesRecord[uniqueId] = uploadFile
            pendingFilesList.push(uploadFile)
          })

          // Apply updates to existing files
          const updatedExistingFiles = { ...currentFiles }
          Object.entries(filesToUpdate).forEach(([fileId, updates]) => {
            if (updatedExistingFiles[fileId]) {
              updatedExistingFiles[fileId] = {
                ...updatedExistingFiles[fileId],
                ...updates,
              }
            }
          })

          set(
            { files: { ...updatedExistingFiles, ...pendingFilesRecord } },
            false,
            'addFiles:pending'
          )

          Promise.all(
            pendingFilesList.map(async (uploadFile) => {
              if (uploadFile.validationStatus === FileValidationStatus.FAILED) {
                return
              }

              const currentState = get().files
              const currentFile = currentState[uploadFile.id]

              if (
                !currentFile ||
                currentFile.validationStatus === FileValidationStatus.CANCELLED
              ) {
                return
              }

              const allFiles = values(currentState)
              const filesWithSameName = allFiles.filter(
                (f) => f.fileName === uploadFile.fileName
              )

              if (filesWithSameName.length > 1) {
                filesWithSameName.forEach((f) => {
                  get().updateFile(f.id, {
                    validationStatus: FileValidationStatus.FAILED,
                    errorMessage: 'Duplicate File Name.',
                    versionStatus: undefined,
                    isActionRequired: false,
                  })
                })
                return
              }

              const { validationStatus, versionStatus, errorMessage } =
                validateFile(uploadFile.file)

              const detectedType = getDocumentTypeFromFilename(
                uploadFile.fileName
              )
              const requiresManualType = !detectedType
              const hasAutoDetectedType = !!detectedType

              let finalValidationStatus = validationStatus
              let finalErrorMessage = errorMessage
              let isActionRequired: boolean | undefined = undefined

              if (
                requiresManualType &&
                validationStatus === FileValidationStatus.READY_TO_UPLOAD
              ) {
                finalValidationStatus = FileValidationStatus.ACTION_REQUIRED
                finalErrorMessage = undefined
                isActionRequired = true
              } else if (
                hasAutoDetectedType &&
                validationStatus === FileValidationStatus.READY_TO_UPLOAD
              ) {
                finalValidationStatus = FileValidationStatus.VALIDATING
              }

              get().updateFile(uploadFile.id, {
                validationStatus: finalValidationStatus,
                versionStatus,
                errorMessage: finalErrorMessage,
                documentType: detectedType,
                requiresManualType,
                hasAutoDetectedType,
                isActionRequired,
                validationApiCalled: false,
              })
            })
          ).then(async () => {
            const state = get()
            const deliverableSet =
              state.selectedDeliverableSet ||
              (deliverableSetId ? { id: deliverableSetId } : null)

            if (!deliverableSet?.id) {
              const filesToUpdate = values(state.files).filter(
                (f) =>
                  f.validationStatus === FileValidationStatus.VALIDATING &&
                  f.documentType
              )
              filesToUpdate.forEach((file) => {
                get().updateFile(file.id, {
                  validationStatus: FileValidationStatus.READY_TO_UPLOAD,
                })
              })
              return
            }

            const filesToValidate = values(state.files).filter(
              (f) =>
                !f.validationApiCalled &&
                f.validationStatus === FileValidationStatus.VALIDATING &&
                f.documentType
            )

            if (filesToValidate.length === 0) {
              return
            }

            try {
              const fileBatches = chunkArray(
                filesToValidate,
                UPLOAD_FILE_BATCH_SIZE
              )

              for (const batch of fileBatches) {
                const validBatch = batch.filter((file) => {
                  const currentFile = get().files[file.id]
                  return (
                    currentFile?.validationStatus ===
                    FileValidationStatus.VALIDATING
                  )
                })

                if (validBatch.length === 0) {
                  continue
                }

                const uploadResponse = await fileUploadApi.requestUploadUrls({
                  deliverable_set_id: deliverableSet.id,
                  files: validBatch.map((file) => ({
                    document_type: file.documentType || 'other',
                    filename: file.fileName,
                    document_id: file.documentId || null,
                  })),
                })

                await timeout(UPLOAD_DELAY_MS)

                const containerData = uploadResponse.data[0]
                if (containerData?.upload_urls) {
                  validBatch.forEach((file, index) => {
                    const uploadUrlData = containerData.upload_urls[index]
                    if (uploadUrlData) {
                      const currentFile = get().files[file.id]
                      if (
                        !currentFile ||
                        currentFile.validationStatus ===
                          FileValidationStatus.CANCELLED
                      ) {
                        return
                      }

                      get().updateFile(file.id, {
                        documentId: uploadUrlData.document_id,
                        fileIndex: uploadUrlData.file_index,
                        versionNumber: uploadUrlData.version_number,
                        isInitial: uploadUrlData.is_initial,
                        versionStatus: uploadUrlData.is_initial
                          ? getNewFileVersionStatus()
                          : getNewVersionStatus(),
                        blobName: uploadUrlData.blob_name,
                        blobUrl: uploadUrlData.blob_url,
                        validationApiCalled: true,
                        validationStatus: FileValidationStatus.READY_TO_UPLOAD,
                      })
                    }
                  })
                }
              }
            } catch {
              // Ignore
            }
          })
        },
        addErrorFiles: (errorFiles: ExtractionError[]) => {
          errorFiles.forEach((errorFile, index) => {
            const baseTimestamp = Date.now()
            const randomValue = crypto
              .getRandomValues(new Uint32Array(1))[0]
              .toString(36)
            const uniqueId = `${errorFile.fileName}_${index}_${baseTimestamp}_${randomValue}`
            const uploadFile: UploadFile = {
              id: uniqueId,
              file: errorFile.file ?? new File([], errorFile.fileName),
              fileName: errorFile.fileName,
              fileSize: errorFile.file ? errorFile.file.size : 0,
              fileType: errorFile.file ? errorFile.file.type : '',
              validationStatus: FileValidationStatus.FAILED,
              uploadProgress: 0,
              documentType: '',
              errorMessage: 'Unable to extract the file',
            }
            set(
              { files: { ...get().files, [uniqueId]: uploadFile } },
              false,
              'addErrorFiles'
            )
          })
        },

        removeFile: (fileId: string) => {
          const currentFiles = get().files
          const fileToRemove = currentFiles[fileId]

          // Get the file name before removal
          const removedFileName = fileToRemove?.fileName

          // Remove the file
          const remainingFiles = omit(currentFiles, fileId)
          set({ files: remainingFiles }, false, 'removeFile')

          // If no file was found or no name, exit early
          if (!removedFileName) return

          // Step 1: Find all files with FAILED status and same name as removed file
          const filesToRevalidate = (values(remainingFiles) as UploadFile[]) // NOSONAR
            .filter(
              (file) =>
                file.validationStatus === FileValidationStatus.FAILED &&
                file.fileName === removedFileName
            )

          // If no files found, exit early
          if (filesToRevalidate.length === 0) return

          // Step 2: Remove all found files from the store
          const fileIdsToRemove = filesToRevalidate.map(
            (file: UploadFile) => file.id
          )
          const filesAfterRemoval = omit(remainingFiles, fileIdsToRemove)
          set(
            { files: filesAfterRemoval },
            false,
            'removeFile:removeFailedDuplicates'
          )

          // Step 3: Re-validate by calling addFiles with the File objects
          const filesToAdd = filesToRevalidate.map(
            (file: UploadFile) => file.file
          )
          if (filesToAdd.length > 0) get().addFiles(filesToAdd)
        },

        cancelFileValidation: (fileId: string) => {
          const currentFiles = get().files
          const file = currentFiles[fileId]

          if (file?.validationStatus !== FileValidationStatus.VALIDATING) {
            return
          }

          const updatedFiles = {
            ...currentFiles,
            [fileId]: {
              ...file,
              validationStatus: FileValidationStatus.CANCELLED,
              errorMessage: undefined,
            },
          }
          set({ files: updatedFiles }, false, 'cancelFileValidation')
        },

        removeFailedAndCancelledFiles: () => {
          const currentFiles = get().files
          const filteredFiles = pickBy(
            currentFiles,
            (file) =>
              file.validationStatus !== FileValidationStatus.FAILED &&
              file.validationStatus !== FileValidationStatus.CANCELLED
          )
          set({ files: filteredFiles }, false, 'removeFailedAndCancelledFiles')
        },

        retryFailedFiles: () => {
          const currentFiles = get().files
          const updatedFiles = { ...currentFiles }

          Object.entries(updatedFiles).forEach(([fileId, file]) => {
            if (file.validationStatus === FileValidationStatus.FAILED) {
              updatedFiles[fileId] = {
                ...file,
                validationStatus: FileValidationStatus.VALIDATING,
                uploadProgress: 0,
                errorMessage: undefined,
              }
            }
          })

          set({ files: updatedFiles }, false, 'retryFailedFiles')

          // Re-run validation logic
          const filesToRetry = values(updatedFiles).filter(
            (f) => f.validationStatus === FileValidationStatus.VALIDATING
          )

          filesToRetry.forEach(async (file) => {
            const { validationStatus, versionStatus, errorMessage } =
              validateFile(file.file)
            const detectedType = getDocumentTypeFromFilename(file.fileName)
            const requiresManualType = !detectedType

            let finalValidationStatus = validationStatus
            if (
              requiresManualType &&
              validationStatus === FileValidationStatus.READY_TO_UPLOAD
            ) {
              finalValidationStatus = FileValidationStatus.ACTION_REQUIRED
            }

            get().updateFile(file.id, {
              validationStatus: finalValidationStatus,
              versionStatus,
              errorMessage,
              documentType: detectedType,
              requiresManualType,
              validationApiCalled: false,
            })
          })
        },

        clearFiles: () => {
          set({ files: {} }, false, 'clearFiles')
        },

        /**
         * Update a specific file's properties - O(1) update with Record
         */
        updateFile: (fileId: string, updates: Partial<UploadFile>) => {
          const currentFiles = get().files
          const file = currentFiles[fileId]
          if (!file) return

          set(
            { files: { ...currentFiles, [fileId]: { ...file, ...updates } } },
            false,
            'updateFile'
          )
        },

        updateFileType: async (fileId: string, documentType: string) => {
          const currentFiles = get().files
          const file = currentFiles[fileId]
          if (!file) return

          const updates: Partial<UploadFile> = {
            documentType,
            requiresManualType: false,
            validationApiCalled: false,
          }

          // Keep the current validation status (don't change ACTION_REQUIRED to READY_TO_UPLOAD)
          if (
            file.validationStatus === FileValidationStatus.ACTION_REQUIRED &&
            documentType
          ) {
            updates.errorMessage = undefined
          }

          set(
            { files: { ...currentFiles, [fileId]: { ...file, ...updates } } },
            false,
            'updateFileType'
          )

          const state = get()
          const deliverableSet =
            state.selectedDeliverableSet ||
            (state.contextDeliverableSetId
              ? { id: state.contextDeliverableSetId }
              : null)

          if (!deliverableSet?.id || !documentType) {
            return
          }

          try {
            const updatedFile = state.files[fileId]

            if (!updatedFile) return

            const uploadResponse = await fileUploadApi.requestUploadUrls({
              deliverable_set_id: deliverableSet.id,
              files: [
                {
                  document_type: documentType,
                  filename: updatedFile.fileName,
                  document_id: updatedFile.documentId || null,
                },
              ],
            })

            const containerData = uploadResponse.data[0]
            if (containerData?.upload_urls?.[0]) {
              const uploadUrlData = containerData.upload_urls[0]

              const currentFile = get().files[fileId]
              if (!currentFile) {
                return
              }

              get().updateFile(fileId, {
                documentId: uploadUrlData.document_id,
                fileIndex: uploadUrlData.file_index,
                versionNumber: uploadUrlData.version_number,
                isInitial: uploadUrlData.is_initial,
                versionStatus: uploadUrlData.is_initial
                  ? getNewFileVersionStatus()
                  : getNewVersionStatus(),
                blobName: uploadUrlData.blob_name,
                blobUrl: uploadUrlData.blob_url,
                validationApiCalled: true,
                validationStatus: FileValidationStatus.ACTION_REQUIRED,
              })
            }
          } catch {
            // Ignore
          }
        },

        setSelectedDeliverableSet: (
          deliverableSet: DeliverableSetOption | null
        ) => {
          set(
            { selectedDeliverableSet: deliverableSet },
            false,
            'setSelectedDeliverableSet'
          )
        },

        startUpload: () => {
          const currentFiles = get().files

          const updatedFiles = Object.fromEntries(
            Object.entries(currentFiles).map(([id, file]) => [
              id,
              file.validationStatus === FileValidationStatus.READY_TO_UPLOAD ||
              (file.validationStatus === FileValidationStatus.ACTION_REQUIRED &&
                file.documentType)
                ? { ...file, validationStatus: FileValidationStatus.UPLOADING }
                : file,
            ])
          )

          set(
            {
              files: updatedFiles,
              isUploading: true,
              uploadProgress: 0,
              error: null,
              showFloatingIndicator: true,
              uploadPhase: UploadPhase.UPLOAD,
            },
            false,
            'startUpload'
          )
        },

        updateUploadProgress: (progress: number) => {
          set({ uploadProgress: progress }, false, 'updateUploadProgress')
        },

        completeUpload: () => {
          set(
            {
              isUploading: false,
              uploadProgress: 100,
              showFloatingIndicator: false,
            },
            false,
            'completeUpload'
          )
        },

        /**
         * Cancel the upload process
         */
        cancelUpload: () => {
          const currentFiles = get().files
          // Mark all files as cancelled
          const updatedFiles = Object.fromEntries(
            Object.entries(currentFiles).map(([id, file]) => [
              id,
              file.validationStatus === FileValidationStatus.VALIDATING ||
              file.validationStatus === FileValidationStatus.UPLOADING
                ? {
                    ...file,
                    validationStatus: FileValidationStatus.CANCELLED,
                    errorMessage: undefined,
                  }
                : file,
            ])
          )

          set(
            {
              files: updatedFiles,
              isUploading: false,
              uploadProgress: 0,
              showFloatingIndicator: false,
              uploadPhase: UploadPhase.VALIDATION,
            },
            false,
            'cancelUpload'
          )
        },

        /**
         * Set error message
         */
        setError: (error: string | null) => {
          set({ error }, false, 'setError')
        },

        /**
         * Clear error message
         */
        clearError: () => {
          set({ error: null }, false, 'clearError')
        },

        /**
         * Set floating indicator visibility
         */
        setFloatingIndicator: (show: boolean) => {
          set({ showFloatingIndicator: show }, false, 'setFloatingIndicator')
        },

        /**
         * Set filter values
         */
        setFilter: (filter: Partial<FileUploadFilters>) => {
          const currentFilters = get().filters
          set({ filters: { ...currentFilters, ...filter } }, false, 'setFilter')
        },

        /**
         * Reset filters to initial state
         */
        resetFilters: () => {
          set({ filters: initialState.filters }, false, 'resetFilters')
        },

        /**
         * Set context deliverable set ID (for pre-populated mode)
         */
        setContextDeliverableSetId: (id: string | null) => {
          set(
            { contextDeliverableSetId: id },
            false,
            'setContextDeliverableSetId'
          )
        },

        /**
         * Set upload phase
         */
        setUploadPhase: (phase: UploadPhase) => {
          set({ uploadPhase: phase }, false, 'setUploadPhase')
        },

        /**
         * Set upload route path (original page where upload started)
         */
        setUploadRoutePath: (path: string | null) => {
          set({ uploadRoutePath: path }, false, 'setUploadRoutePath')
        },

        /**
         * Set referrer route (page where upload button was clicked)
         */
        setReferrerRoute: (route: string | null) => {
          set({ referrerRoute: route }, false, 'setReferrerRoute')
        },

        reset: () => {
          set(initialState, false, 'reset')
        },
      }),
      {
        name: 'file-upload-storage',
        partialize: (state) => ({
          selectedDeliverableSet: state.selectedDeliverableSet,
          contextDeliverableSetId: state.contextDeliverableSetId,
        }),
      }
    ),
    {
      name: 'FileUploadStore',
    }
  )
)

export const useFileUploadFiles = () => {
  const filesRecord = useFileUploadStore((state) => state.files)
  return useMemo(() => values(filesRecord), [filesRecord])
}
